import { Component, OnDestroy, OnInit, inject, Input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ModalController } from '@ionic/angular';
import { APP_CONFIG, IAppConfig } from '@tcc-mono/shared/app-config';
import { Color, Stable } from '@tcc-mono/vandijk/shared/data-access/models';
import {
  ColorService,
  OriginUbnAnimal,
  OriginUbnAnimalSortBy,
  OriginUbnService,
  OriginUbnStats,
  OriginUbnTraderPeriod,
  OriginUbnTraderRelation,
  OriginUbnTraderRide,
  OriginUbnTraderRidePurchaseOrder,
  RvoUbnInfoService,
  StableService,
  formatGrowthIndexPct,
  GROWTH_INDEX_TYPES,
  growthIndexTone,
} from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { StableDialogComponent } from '@tcc-mono/vandijk/web/crm/feature/stables/stable-dialog';
import { Subject, forkJoin, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { bedrijfstypeLabel } from './animal-info.utils';

@Component({
  selector: 'tcc-mono-rvo-ubn-map-dialog',
  templateUrl: './rvo-ubn-map-dialog.component.html',
  styleUrls: ['./rvo-ubn-map-dialog.component.scss']
})
export class RvoUbnMapDialogComponent implements OnInit, OnDestroy {
  private readonly _rvoUbnInfo = inject(RvoUbnInfoService);
  private readonly _originUbnService = inject(OriginUbnService);
  private readonly _stableService = inject(StableService);
  private readonly _colorService = inject(ColorService);
  private readonly _modalCtrl = inject(ModalController);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _appConfig: IAppConfig = inject(APP_CONFIG);
  private readonly _destroy$ = new Subject<void>();
  private readonly _animalsLoad$ = new Subject<{ ubn: string; page: number }>();
  private readonly _tradersLoad$ = new Subject<{ ubn: string; period: OriginUbnTraderPeriod }>();
  private _tradersLoadedFor: { ubn: string; period: OriginUbnTraderPeriod } | null = null;

  @Input() public ubn = '';
  @Input() public fallbackAddress: string | null = null;

  public loading = true;
  public error: string | null = null;
  public mapEmbedUrl: SafeResourceUrl | null = null;
  public externalMapUrl: string | null = null;
  public holderName: string | null = null;
  public addressLine: string | null = null;
  public rvoAddress: string | null = null;
  public rvoZipcode: string | null = null;
  public rvoCity: string | null = null;
  public existingStable: Stable | null = null;
  public stableLookupLoading = false;
  public bedrijfstypeCode: string | null = null;
  public bedrijfstypeLabelText: string | null = null;

  public isVeehouder = false;
  public animalsLoading = false;
  public animalsError: string | null = null;
  public stats: OriginUbnStats | null = null;
  public animals: OriginUbnAnimal[] = [];
  public withGrowthOnly = true;
  public animalGenderFilter = '';
  public animalColorFilter = '';
  public animalSortBy: OriginUbnAnimalSortBy | null = null;
  public animalSortDirection: 'asc' | 'desc' = 'desc';
  public colors: Color[] = [];
  public page = 1;
  public lastPage = 1;
  public total = 0;
  public readonly growthIndexTypes = GROWTH_INDEX_TYPES;
  public readonly formatGrowthIndexPct = formatGrowthIndexPct;
  public readonly growthIndexTone = growthIndexTone;

  public activeTab: 'animals' | 'traders' = 'animals';
  public tradersLoading = false;
  public tradersError: string | null = null;
  public traders: OriginUbnTraderRelation[] = [];
  public traderPeriod: OriginUbnTraderPeriod = 'year';
  public expandedTraderId: number | null = null;
  public traderRidesLoading = false;
  public traderRidesError: string | null = null;
  public traderRides: OriginUbnTraderRide[] = [];

  constructor() {
    this._animalsLoad$
      .pipe(
        tap(() => {
          this.animalsLoading = true;
          this.animalsError = null;
        }),
        switchMap(({ ubn, page }) =>
          forkJoin({
            stats: this._originUbnService.stats(ubn).pipe(
              catchError(() => {
                this.animalsError = 'Statistieken konden niet worden geladen.';
                return of(null);
              }),
            ),
            animals: this._originUbnService
              .animals(ubn, {
                with_growth_only: this.withGrowthOnly,
                gender: this.animalGenderFilter,
                color: this.animalColorFilter,
                page,
                per_page: 50,
                sort_by: this.animalSortBy ?? undefined,
                sort_direction: this.animalSortBy ? this.animalSortDirection : undefined,
              })
              .pipe(
                catchError(() => {
                  this.animalsError = 'Dierenlijst kon niet worden geladen.';
                  return of(null);
                }),
              ),
          }),
        ),
        takeUntil(this._destroy$),
      )
      .subscribe(({ stats, animals }) => {
        this.animalsLoading = false;
        this.stats = stats?.data ?? null;
        if (animals) {
          this.animals = animals.data ?? [];
          this.page = animals.meta?.current_page ?? 1;
          this.lastPage = animals.meta?.last_page ?? 1;
          this.total = animals.meta?.total ?? 0;
        } else {
          this.animals = [];
        }
      });

    this._tradersLoad$
      .pipe(
        tap(({ ubn, period }) => {
          this.tradersLoading = true;
          this.tradersError = null;
          this._tradersLoadedFor = { ubn, period };
          this.resetTraderRides();
        }),
        switchMap(({ ubn, period }) =>
          this._originUbnService.traderRelations(ubn, period).pipe(
            catchError(() => {
              this.tradersError = 'Handelaren konden niet worden geladen.';
              return of(null);
            }),
          ),
        ),
        takeUntil(this._destroy$),
      )
      .subscribe((response) => {
        this.tradersLoading = false;
        this.traders = response?.data ?? [];
      });
  }

  public ngOnInit(): void {
    this._colorService
      .getAll({ per_page: 500 })
      .pipe(
        map(({ data }) => [...(data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'nl'))),
        takeUntil(this._destroy$),
      )
      .subscribe((colors) => {
        this.colors = colors;
      });

    const ubn = this.ubn?.trim();
    if (!ubn) {
      this.loading = false;
      this.error = 'Geen UBN opgegeven.';
      return;
    }

    this.lookupExistingStable(ubn);

    this._rvoUbnInfo.ubnInfo(ubn).pipe(takeUntil(this._destroy$)).subscribe({
      next: (items) => {
        this.loading = false;
        const details = items[0];
        if (!details) {
          this.error = `Geen RVO-gegevens gevonden voor UBN ${ubn}.`;
          this.addressLine = this.fallbackAddress;
          return;
        }

        const addressParts = [details.adresOmsME, details.postcodeME, details.plaatsnaamME].filter(
          (part) => !!part?.trim(),
        );
        const address = addressParts.length > 0 ? addressParts.join(', ') : this.fallbackAddress;

        this.holderName = details.naamHouder?.trim() || null;
        this.rvoAddress = details.adresOmsME?.trim() || null;
        this.rvoZipcode = details.postcodeME?.trim() || null;
        this.rvoCity = details.plaatsnaamME?.trim() || null;
        this.addressLine = address;
        const typeCode = details.typeBedrijfsvestiging?.trim() || null;
        this.bedrijfstypeCode = typeCode;
        this.bedrijfstypeLabelText = typeCode ? bedrijfstypeLabel(typeCode) : null;

        const latitude = details.vdvLatitude;
        const longitude = details.vdvLongitude;
        if (latitude == null || longitude == null) {
          this.error = address
            ? `Geen kaartcoördinaten bij RVO voor UBN ${ubn}. Adres: ${address}`
            : `Geen kaartcoördinaten bij RVO voor UBN ${ubn}.`;
        } else {
          this.error = null;
          this.mapEmbedUrl = this._buildEmbedUrl(latitude, longitude);
          this.externalMapUrl = `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=16/${latitude}/${longitude}`;
        }

        this.isVeehouder = (typeCode ?? '').toUpperCase() === 'VH';
        if (this.isVeehouder) {
          this.loadAnimals(ubn, 1);
        }
      },
      error: () => {
        this.loading = false;
        this.error = 'UBN-locatie kon niet worden opgehaald bij RVO.';
        this.addressLine = this.fallbackAddress;
      },
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._animalsLoad$.complete();
    this._tradersLoad$.complete();
  }

  public setActiveTab(tab: 'animals' | 'traders'): void {
    this.activeTab = tab;
    if (tab === 'traders') {
      this.ensureTradersLoaded();
    }
  }

  public setTraderPeriod(period: OriginUbnTraderPeriod): void {
    if (this.traderPeriod === period) {
      return;
    }
    this.traderPeriod = period;
    this._tradersLoadedFor = null;
    this.resetTraderRides();
    if (this.activeTab === 'traders') {
      this.ensureTradersLoaded(true);
    }
  }

  public reloadTraders(): void {
    this.resetTraderRides();
    this.ensureTradersLoaded(true);
  }

  public toggleTraderRides(trader: OriginUbnTraderRelation): void {
    if (this.expandedTraderId === trader.id) {
      this.resetTraderRides();
      return;
    }
    this.loadTraderRides(trader);
  }

  public rideHref(ride: OriginUbnTraderRide): string {
    return `/app/office/kvs-rides/${ride.id}`;
  }

  public purchaseOrderHref(order: OriginUbnTraderRidePurchaseOrder): string {
    return `/app/office/purchase-orders/${order.id}`;
  }

  public formatRideOrigin(origin: string | null): string {
    if (origin === 'kvs') {
      return 'KVS';
    }
    if (origin === 'trader') {
      return 'Trader-app';
    }
    if (origin === 'erp') {
      return 'ERP';
    }
    return origin || '—';
  }

  public rideHasOtherRelation(ride: OriginUbnTraderRide): boolean {
    return this.expandedTraderId != null && ride.relation_id !== this.expandedTraderId;
  }

  public formatTraderDate(value: string | null): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return date.toLocaleDateString('nl-NL');
  }

  public onWithGrowthOnlyChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.withGrowthOnly = checked;
    const ubn = this.ubn?.trim();
    if (ubn && this.isVeehouder) {
      this.loadAnimals(ubn, 1);
    }
  }

  public goToPage(page: number): void {
    const ubn = this.ubn?.trim();
    if (!ubn || page < 1 || page > this.lastPage) {
      return;
    }
    this.loadAnimals(ubn, page);
  }

  public sortAnimalsBy(field: OriginUbnAnimalSortBy): void {
    if (this.animalSortBy === field) {
      this.animalSortDirection = this.animalSortDirection === 'desc' ? 'asc' : 'desc';
    } else {
      this.animalSortBy = field;
      this.animalSortDirection = 'desc';
    }
    const ubn = this.ubn?.trim();
    if (ubn && this.isVeehouder) {
      this.loadAnimals(ubn, 1);
    }
  }

  public animalSortAria(field: OriginUbnAnimalSortBy): 'ascending' | 'descending' | 'none' {
    if (this.animalSortBy !== field) {
      return 'none';
    }
    return this.animalSortDirection === 'asc' ? 'ascending' : 'descending';
  }

  public animalSortIcon(field: OriginUbnAnimalSortBy): 'sort' | 'sort-up' | 'sort-down' {
    if (this.animalSortBy !== field) {
      return 'sort';
    }
    return this.animalSortDirection === 'asc' ? 'sort-up' : 'sort-down';
  }

  public onAnimalGenderFilterChange(value: string | null | undefined): void {
    this.animalGenderFilter = value?.trim() || '';
    const ubn = this.ubn?.trim();
    if (ubn && this.isVeehouder) {
      this.loadAnimals(ubn, 1);
    }
  }

  public onAnimalColorFilterChange(value: string | null | undefined): void {
    this.animalColorFilter = value?.trim() || '';
    const ubn = this.ubn?.trim();
    if (ubn && this.isVeehouder) {
      this.loadAnimals(ubn, 1);
    }
  }

  public clearAnimalFilters(): void {
    this.animalGenderFilter = '';
    this.animalColorFilter = '';
    const ubn = this.ubn?.trim();
    if (ubn && this.isVeehouder) {
      this.loadAnimals(ubn, 1);
    }
  }

  public formatGenderLabel(code?: string | null, name?: string | null): string {
    const normalized = String(code || '').trim().toUpperCase();
    if (normalized === 'M') {
      return '♂ Stier';
    }
    if (normalized === 'V') {
      return '♀ Vaars';
    }
    return name || code || '—';
  }

  public formatSlaughterWeight(animal: OriginUbnAnimal): string {
    const weight = animal.slaughter_list_line?.weight;
    if (weight == null || weight === '') {
      return '—';
    }
    if (typeof weight === 'number') {
      return `${weight.toLocaleString('nl-NL', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })} kg`;
    }
    return `${String(weight).replace('.', ',')} kg`;
  }

  public formatDays(value?: number | null): string {
    if (value == null) {
      return '—';
    }
    return value.toLocaleString('nl-NL', {
      maximumFractionDigits: 0,
    });
  }

  public canOpenKoppel(animal: OriginUbnAnimal): boolean {
    return this.koppelHref(animal) != null;
  }

  public koppelHref(animal: OriginUbnAnimal): string | null {
    if (!animal.koppel_id || !animal.identification_number) {
      return null;
    }

    const base = this._resolveKoppelsBaseUrl();
    if (!base) {
      return null;
    }

    const type = animal.koppel_type;
    const typePath =
      type && ['witvlees', 'starters', 'rose'].includes(type) ? type : 'koppels';
    const params = new URLSearchParams({
      highlightAnimalId: String(animal.id),
      identification_number: animal.identification_number,
    });

    return `${base}/${typePath}/${animal.koppel_id}?${params.toString()}`;
  }

  private _resolveKoppelsBaseUrl(): string | null {
    const fromConfig = this._appConfig.environments?.find((env) => env.name === 'Koppels')?.url;
    if (fromConfig?.trim()) {
      return fromConfig.trim().replace(/\/$/, '');
    }

    const hostname = window.location.hostname;
    if (hostname.includes('develop')) {
      return 'https://vandijk-koppels-develop.tccapps.nl';
    }

    return 'https://vandijk-koppels.tccapps.nl';
  }

  public close(): void {
    void this._modalCtrl.dismiss();
  }

  public stableHref(): string | null {
    const id = this.existingStable?.id;
    return id ? `/app/crm/stables/${id}` : null;
  }

  public createStable(): void {
    const ubn = this.ubn?.trim();
    if (!ubn || this.existingStable) {
      return;
    }

    const prefill = new Stable();
    prefill.name = this.holderName || `UBN ${ubn}`;
    prefill.ubn = ubn;
    prefill.address = this.rvoAddress || '';
    prefill.zipcode = this.rvoZipcode || '';
    prefill.city = this.rvoCity || '';
    prefill.country = 'NL';
    prefill.properties = [{ code: 'UBN', value: ubn }];

    from(
      this._modalCtrl.create({
        component: StableDialogComponent,
        componentProps: { stable: prefill },
        cssClass: 'large-dialog-modal stacked-modal',
      }),
    )
      .pipe(
        tap((modal) => modal.present()),
        switchMap((modal) => modal.onDidDismiss()),
        map(({ data }) => data as Stable | undefined),
        filter((stable): stable is Stable => !!stable?.id),
        take(1),
        takeUntil(this._destroy$),
      )
      .subscribe((stable) => {
        this.existingStable = stable;
      });
  }

  private lookupExistingStable(ubn: string): void {
    this.stableLookupLoading = true;
    this._stableService
      .findByUbn(ubn)
      .pipe(
        catchError(() => of(null)),
        takeUntil(this._destroy$),
      )
      .subscribe((stable) => {
        this.stableLookupLoading = false;
        this.existingStable = stable;
      });
  }

  private loadAnimals(ubn: string, page: number): void {
    this._animalsLoad$.next({ ubn, page });
  }

  private ensureTradersLoaded(force = false): void {
    const ubn = this.ubn?.trim();
    if (!ubn) {
      return;
    }
    const period = this.traderPeriod;
    if (
      !force &&
      this._tradersLoadedFor?.ubn === ubn &&
      this._tradersLoadedFor?.period === period
    ) {
      return;
    }
    this._tradersLoad$.next({ ubn, period });
  }

  private loadTraderRides(trader: OriginUbnTraderRelation): void {
    const ubn = this.ubn?.trim();
    if (!ubn) {
      return;
    }
    this.expandedTraderId = trader.id;
    this.traderRidesLoading = true;
    this.traderRidesError = null;
    this.traderRides = [];
    this._originUbnService
      .traderRelationRides(ubn, trader.id, this.traderPeriod)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          if (this.expandedTraderId !== trader.id) {
            return;
          }
          this.traderRidesLoading = false;
          this.traderRides = response?.data ?? [];
        },
        error: () => {
          if (this.expandedTraderId !== trader.id) {
            return;
          }
          this.traderRidesLoading = false;
          this.traderRidesError = 'Ritten konden niet worden geladen.';
        },
      });
  }

  private resetTraderRides(): void {
    this.expandedTraderId = null;
    this.traderRidesLoading = false;
    this.traderRidesError = null;
    this.traderRides = [];
  }

  private _buildEmbedUrl(latitude: number, longitude: number): SafeResourceUrl {
    const pad = 0.008;
    const bbox = [longitude - pad, latitude - pad, longitude + pad, latitude + pad].join(',');
    const url =
      `https://www.openstreetmap.org/export/embed.html` +
      `?bbox=${encodeURIComponent(bbox)}` +
      `&layer=mapnik` +
      `&marker=${latitude}%2C${longitude}`;

    return this._sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
